import React, { useEffect, useMemo, useState } from 'react';
import { Brain, ChevronLeft, ChevronRight, History, Sparkles } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import PlayingCard from '../components/ui/PlayingCard';
import { useAuth } from '../contexts/AuthContext';

type AnalysisMode = 'simple' | 'detailed' | 'solver';
type Street = 'preflop' | 'flop' | 'turn' | 'river';

interface PreloadedHandState {
  preloadedHand?: {
    cards?: string[];
    board?: string[];
    position?: string;
    ev?: number;
    boardText?: string;
    analysis?: string;
  };
}

interface VillainProfile {
  id: string;
  label: string;
  badge: string;
  summary: string;
  exploit: string;
  solverAdjustment: string;
}

interface HandAction {
  id: string;
  street: Street;
  actor: 'hero' | 'villain';
  label: string;
  note: string;
  solverNote: string;
}

interface Scenario {
  key: string;
  heroName: string;
  heroPosition: string;
  heroStack: string;
  villainPosition: string;
  villainStack: string;
  heroCards: [string, string];
  boardByStreet: Record<Street, string[]>;
  potByStreet: Record<Street, string>;
  actionsByStreet: Record<Street, HandAction[]>;
  initialStreet: Street;
  summary: string;
  format: string;
  modality: string;
}

const STREET_LABELS: Record<Street, string> = {
  preflop: 'PRÉ-FLOP',
  flop: 'FLOP',
  turn: 'TURN',
  river: 'RIVER',
};

const ANALYSIS_MODES: Array<{
  value: AnalysisMode;
  label: string;
  helper: string;
}> = [
  {
    value: 'simple',
    label: 'Simples',
    helper: 'Replay limpo da mão, sem parecer da IA.',
  },
  {
    value: 'detailed',
    label: 'Detalhado',
    helper: 'Leitura heurística do coach com contexto lateral.',
  },
  {
    value: 'solver',
    label: 'Solver',
    helper: 'Permite trocar a mão do herói e comparar a linha.',
  },
];

const VILLAIN_PROFILES: VillainProfile[] = [
  {
    id: 'regular_aggressive',
    label: 'Regular Agressivo',
    badge: 'REG AGG',
    summary: 'Pressiona ranges capados, defende bem versus sizings pequenos e pune atrasos de agressão.',
    exploit: 'Vale proteger checks atrasados e escolher sizings que não deixem turns fáceis para float.',
    solverAdjustment: 'Com blockers fortes, a linha ótima mantém mais apostas pequenas e raises seletivos.',
  },
  {
    id: 'calling_station',
    label: 'Calling Station',
    badge: 'CALLER',
    summary: 'Paga demais em streets iniciais e desacelera pouco com pares médios.',
    exploit: 'Você pode ampliar value thin e reduzir blefes puros em runouts estáticos.',
    solverAdjustment: 'A simulação valoriza mãos que ganham no showdown e corta semiblefes marginais.',
  },
  {
    id: 'nit',
    label: 'Nit / Tight',
    badge: 'NIT',
    summary: 'Entra em poucos potes, range forte no call pré-flop e folds rápidos sob pressão pós-flop.',
    exploit: 'Bluffs bem bloqueados aumentam de valor, especialmente quando ele capou a range no flop.',
    solverAdjustment: 'O solver tende a elevar a pressão em boards secos e checkar mais valor médio.',
  },
];

const CARD_OPTIONS = (() => {
  const ranks = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
  const suits = ['s', 'h', 'd', 'c'];
  return ranks.flatMap((rank) => suits.map((suit) => `${rank}${suit}`));
})();

function parseBoardCards(text?: string) {
  if (!text) return [];
  return (text.match(/[2-9TJQKA][shdc]/gi) || []).slice(0, 5);
}

function normalizeCardList(cards?: string[]) {
  return (cards ?? [])
    .map((card) => card.trim())
    .filter((card) => /^[2-9TJQKA][shdc]$/i.test(card))
    .map((card) => `${card[0].toUpperCase()}${card[1].toLowerCase()}`);
}

function parsePositions(position?: string) {
  const clean = position?.replace(/\s+/g, ' ').trim() ?? '';
  const [heroRaw, villainRaw] = clean.split(/\s+vs\s+/i);

  return {
    heroPosition: heroRaw?.toUpperCase() || 'BTN',
    villainPosition: villainRaw?.toUpperCase() || 'BB',
  };
}

function inferStreet(boardCount: number): Street {
  if (boardCount >= 5) return 'river';
  if (boardCount >= 4) return 'turn';
  if (boardCount >= 3) return 'flop';
  return 'preflop';
}

function buildBoardByStreet(boardCards: string[]) {
  return {
    preflop: [],
    flop: boardCards.slice(0, Math.min(3, boardCards.length)),
    turn: boardCards.slice(0, Math.min(4, boardCards.length)),
    river: boardCards.slice(0, Math.min(5, boardCards.length)),
  } satisfies Record<Street, string[]>;
}

function buildScenario(
  preloadedHand: PreloadedHandState['preloadedHand'] | undefined,
  heroName: string,
  experience: string,
  format: string
): Scenario {
  const fallbackCards: [string, string] = ['Ah', 'Kd'];
  const parsedHeroCards = normalizeCardList(preloadedHand?.cards);
  const heroCards: [string, string] = [
    parsedHeroCards[0] ?? fallbackCards[0],
    parsedHeroCards[1] ?? fallbackCards[1],
  ];
  const boardCards = normalizeCardList(preloadedHand?.board).length
    ? normalizeCardList(preloadedHand?.board)
    : parseBoardCards(preloadedHand?.boardText);
  const { heroPosition, villainPosition } = parsePositions(preloadedHand?.position);
  const initialStreet = inferStreet(boardCards.length);

  return {
    key: `${heroCards.join('-')}-${boardCards.join('-')}-${heroPosition}-${villainPosition}`,
    heroName,
    heroPosition,
    heroStack: '38k',
    villainPosition,
    villainStack: '36k',
    heroCards,
    boardByStreet: buildBoardByStreet(boardCards),
    potByStreet: {
      preflop: '3.5bb',
      flop: '11.3bb',
      turn: '18.9bb',
      river: '31.4bb',
    },
    actionsByStreet: {
      preflop: [
        {
          id: 'pf-1',
          street: 'preflop',
          actor: 'hero',
          label: `${heroPosition}: Raise 2.2bb`,
          note: 'Abertura padrão com boa realização de equidade e iniciativa em posição.',
          solverNote: 'O solver mantém alta frequência de open com mãos fortes e broadways conectados.',
        },
        {
          id: 'pf-2',
          street: 'preflop',
          actor: 'villain',
          label: `${villainPosition}: Call 2.2bb`,
          note: 'O call do vilão mantém um range relativamente capado, com suiteds, broadways e pares médios.',
          solverNote: 'Contra calls mais largos, o plano IP ganha valor com c-bets pequenas em boards secos.',
        },
      ],
      flop: [
        {
          id: 'f-1',
          street: 'flop',
          actor: 'villain',
          label: `${villainPosition}: Check`,
          note: 'O check preserva o range inteiro, mas abre espaço para você capturar EV com sizing eficiente.',
          solverNote: 'A estratégia ótima responde com mix entre bet pequena e checks protegidos.',
        },
        {
          id: 'f-2',
          street: 'flop',
          actor: 'hero',
          label: `${heroPosition}: Bet 2.8bb`,
          note: 'A aposta pequena pressiona floats fracos, mantém mãos piores pagando e nega cartas grátis.',
          solverNote: 'No solver, essa linha cresce quando você bloqueia top pair forte e continuações de raise.',
        },
        {
          id: 'f-3',
          street: 'flop',
          actor: 'villain',
          label: `${villainPosition}: Call 2.8bb`,
          note: 'Depois do call, o range do vilão continua com pares, backdoors e alguns slowplays.',
          solverNote: 'A resposta ótima prepara barrels seletivos e checa mãos médias sem bloqueadores.',
        },
      ],
      turn: [
        {
          id: 't-1',
          street: 'turn',
          actor: 'villain',
          label: `${villainPosition}: Check`,
          note: 'Segundo check enfraquece parte da range e permite explorar cap de showdown médio.',
          solverNote: 'O solver continua apostando mãos com vantagem de nuts e freia bluff-catchers marginais.',
        },
        {
          id: 't-2',
          street: 'turn',
          actor: 'hero',
          label: `${heroPosition}: Check back`,
          note: 'O atraso controla variância e protege sua faixa de check, especialmente com showdown útil.',
          solverNote: 'Com mãos alternativas fortes, a simulação tende a retomar o barrel com frequência maior.',
        },
      ],
      river: [
        {
          id: 'r-1',
          street: 'river',
          actor: 'villain',
          label: `${villainPosition}: Bet 7.5bb`,
          note: 'A lead polariza o range e exige leitura fina de blockers, runout e perfil do vilão.',
          solverNote: 'Na solução padrão, bluff-catchers sem blockers relevantes reduzem bastante a frequência de call.',
        },
        {
          id: 'r-2',
          street: 'river',
          actor: 'hero',
          label: `${heroPosition}: Decisão final`,
          note: 'O ponto principal aqui é comparar sua captura de valor ao risco de dominância no showdown.',
          solverNote: 'Com combinações alternativas, a decisão muda bastante de acordo com blockers de top pair e busted draws.',
        },
      ],
    },
    initialStreet,
    summary:
      preloadedHand?.analysis ??
      'Replay pronto para revisar a linha por street e comparar ajustes contra diferentes perfis de vilão.',
    format,
    modality: 'NLH',
  };
}

function rankValue(card: string) {
  const order: Record<string, number> = {
    A: 14,
    K: 13,
    Q: 12,
    J: 11,
    T: 10,
    '9': 9,
    '8': 8,
    '7': 7,
    '6': 6,
    '5': 5,
    '4': 4,
    '3': 3,
    '2': 2,
  };
  return order[card[0].toUpperCase()] ?? 0;
}

function buildSolverRead(cards: [string, string], profile: VillainProfile, street: Street) {
  const [first, second] = cards;
  const pair = first[0].toUpperCase() === second[0].toUpperCase();
  const suited = first[1].toLowerCase() === second[1].toLowerCase();
  const premiumCount = [first, second].filter((card) => rankValue(card) >= 12).length;
  const connected = Math.abs(rankValue(first) - rankValue(second)) <= 1;

  if (pair && premiumCount === 2) {
    return {
      title: 'Pressão por valor em alta frequência',
      detail: `Com ${cards.join(' ')}, o plano ótimo tende a apostar mais por valor já no ${STREET_LABELS[street].toLowerCase()}, explorando calls dominados do perfil ${profile.label.toLowerCase()}.`,
    };
  }

  if (suited && premiumCount >= 1 && connected) {
    return {
      title: 'Agressão seletiva com boa realização',
      detail: `Essa combinação mantém backdoors fortes e blockers úteis. Contra ${profile.label.toLowerCase()}, a linha costuma preservar apostas pequenas e barrels em cartas boas.`,
    };
  }

  if (premiumCount === 0 && !pair) {
    return {
      title: 'Mais checks e menos variância',
      detail: `Trocando para ${cards.join(' ')}, o solver costuma reduzir a frequência agressiva e aceitar mais checks para não superbluffar contra ${profile.label.toLowerCase()}.`,
    };
  }

  return {
    title: 'Mix entre bet e controle',
    detail: `A mão ${cards.join(' ')} continua jogável, mas o solver distribui mais mixes de bet/check conforme blockers e resposta esperada do ${profile.label.toLowerCase()}.`,
  };
}

function placeholderCards(amount: number) {
  return Array.from({ length: amount }).map((_, index) => (
    <div
      key={`placeholder-${index}`}
      style={{
        width: 38,
        height: 52,
        borderRadius: 8,
        border: '1px dashed rgba(255,255,255,0.16)',
        background: 'rgba(255,255,255,0.03)',
      }}
    />
  ));
}

export default function IACoach() {
  const location = useLocation();
  const { user } = useAuth();
  const locationState = location.state as PreloadedHandState | null;
  const preloadedHand = locationState?.preloadedHand;
  const userExperience = user?.experience ?? 'Intermediário';
  const userFormat = user?.game_type && user.game_type !== 'Iniciante' ? user.game_type : 'MTT';

  const scenario = useMemo(
    () =>
      buildScenario(
        preloadedHand,
        user?.name ?? 'Hero',
        userExperience,
        userFormat
      ),
    [preloadedHand, user?.name, userExperience, userFormat]
  );

  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('detailed');
  const [villainProfileId, setVillainProfileId] = useState<string>(VILLAIN_PROFILES[0].id);
  const [selectedStreet, setSelectedStreet] = useState<Street>(scenario.initialStreet);
  const [selectedActionIndexByStreet, setSelectedActionIndexByStreet] = useState<Record<Street, number>>({
    preflop: 0,
    flop: 0,
    turn: 0,
    river: 0,
  });
  const [solverCards, setSolverCards] = useState<[string, string]>(scenario.heroCards);

  useEffect(() => {
    setSelectedStreet(scenario.initialStreet);
    setSelectedActionIndexByStreet({
      preflop: 0,
      flop: 0,
      turn: 0,
      river: 0,
    });
    setSolverCards(scenario.heroCards);
  }, [scenario.heroCards, scenario.initialStreet, scenario.key]);

  const villainProfile =
    VILLAIN_PROFILES.find((profile) => profile.id === villainProfileId) ??
    VILLAIN_PROFILES[0];
  const currentActions = scenario.actionsByStreet[selectedStreet];
  const currentIndex = Math.min(
    selectedActionIndexByStreet[selectedStreet] ?? 0,
    Math.max(currentActions.length - 1, 0)
  );
  const selectedAction = currentActions[currentIndex];
  const boardCards = scenario.boardByStreet[selectedStreet];
  const heroCards = analysisMode === 'solver' ? solverCards : scenario.heroCards;
  const solverRead = buildSolverRead(solverCards, villainProfile, selectedStreet);
  const actionProgress = `${currentIndex + 1}/${currentActions.length}`;
  const highlightSessionsNav = Boolean(preloadedHand);

  const actionCommentary =
    analysisMode === 'simple'
      ? 'Modo simples ativo: replay manual da ação, sem parecer da IA.'
      : `${selectedAction.note} ${villainProfile.exploit}`;

  function updateActionIndex(nextIndex: number) {
    setSelectedActionIndexByStreet((prev) => ({
      ...prev,
      [selectedStreet]: Math.max(0, Math.min(nextIndex, currentActions.length - 1)),
    }));
  }

  function handleStreetClick(street: Street) {
    setSelectedStreet(street);
  }

  function renderProfileCards() {
    if (analysisMode === 'simple') {
      return (
        <div className="pm-coach-side-card">
          <div className="pm-coach-side-label">Replay Manual</div>
          <p className="pm-coach-side-copy">
            Você controla a street e a ação manualmente. O painel lateral fica
            limpo para rever mãos antigas sem interferência heurística.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="pm-coach-side-card">
          <div className="pm-coach-side-label">Contexto</div>
          <div className="pm-coach-identity">
            <div className="pm-coach-avatar">{scenario.heroName.slice(0, 1).toUpperCase()}</div>
            <div>
              <div className="pm-coach-identity-name">{scenario.heroName}</div>
              <div className="pm-coach-identity-meta">
                {userExperience} · Plano {(user?.plan ?? 'pro').toUpperCase()}
              </div>
            </div>
          </div>
          <div className="pm-coach-kv-grid">
            <div>
              <span>Nível</span>
              <strong>{userExperience}</strong>
            </div>
            <div>
              <span>Formato</span>
              <strong>{scenario.format}</strong>
            </div>
            <div>
              <span>Modalidade</span>
              <strong>{scenario.modality}</strong>
            </div>
            <div>
              <span>Profundidade</span>
              <strong>
                {analysisMode === 'detailed' ? 'Detalhado' : 'Solver'}
              </strong>
            </div>
          </div>
        </div>

        <div className="pm-coach-side-card">
          <div className="pm-coach-side-label">Perfil do Vilão</div>
          <div className="pm-coach-badge-line">
            <span className="pm-coach-badge">{villainProfile.badge}</span>
            <strong>{villainProfile.label}</strong>
          </div>
          <p className="pm-coach-side-copy">{villainProfile.summary}</p>
          <p className="pm-coach-side-copy accent">{villainProfile.exploit}</p>
        </div>

        <div className="pm-coach-side-card">
          <div className="pm-coach-side-label">Leitura do Coach</div>
          <p className="pm-coach-side-copy">{actionCommentary}</p>
          <div className="pm-coach-small-note">{selectedAction.solverNote}</div>
        </div>
      </>
    );
  }

  return (
    <PageLayout activeRoute={highlightSessionsNav ? '/sessions' : '/coach'} bare>
      <style>{`
        .pm-coach-shell {
          min-height: 100vh;
          background:
            radial-gradient(circle at top left, rgba(0,230,118,0.08), transparent 28%),
            radial-gradient(circle at top right, rgba(0,155,255,0.08), transparent 28%),
            #090b0c;
          color: #fff;
          font-family: 'Barlow', sans-serif;
          padding: 26px 24px 28px;
          overflow-y: auto;
        }
        .pm-coach-topbar {
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: flex-start;
          margin-bottom: 18px;
        }
        .pm-coach-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 6px;
        }
        .pm-coach-title h1 {
          margin: 0;
          font-size: 34px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -0.03em;
        }
        .pm-coach-online {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(0,230,118,0.12);
          border: 1px solid rgba(0,230,118,0.24);
          color: #29f78d;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .pm-coach-online span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: currentColor;
        }
        .pm-coach-subtitle {
          margin: 0;
          max-width: 760px;
          color: rgba(255,255,255,0.62);
          font-size: 14px;
          line-height: 1.6;
        }
        .pm-coach-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .pm-coach-ghost-btn,
        .pm-coach-primary-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 14px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.03);
          color: rgba(255,255,255,0.86);
        }
        .pm-coach-primary-link {
          background: linear-gradient(135deg, rgba(0,230,118,0.18), rgba(0,155,255,0.12));
          border-color: rgba(0,230,118,0.24);
        }
        .pm-coach-control-row,
        .pm-coach-solver-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: stretch;
          margin-bottom: 16px;
        }
        .pm-coach-control-card,
        .pm-coach-solver-card {
          min-width: 220px;
          background: rgba(17,19,21,0.9);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          padding: 14px 16px;
          box-shadow: 0 18px 40px rgba(0,0,0,0.24);
        }
        .pm-coach-control-card.grow {
          flex: 1;
          min-width: 280px;
        }
        .pm-coach-label {
          display: block;
          margin-bottom: 8px;
          color: rgba(255,255,255,0.42);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .pm-coach-select {
          width: 100%;
          background: #14181a;
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 15px;
          font-weight: 700;
          outline: none;
        }
        .pm-coach-helper {
          margin-top: 8px;
          color: rgba(255,255,255,0.48);
          font-size: 13px;
          line-height: 1.5;
        }
        .pm-coach-pill-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .pm-coach-pill {
          padding: 11px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.84);
          font-size: 13px;
          font-weight: 700;
        }
        .pm-coach-main-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 340px;
          gap: 18px;
          align-items: start;
        }
        .pm-coach-table-card,
        .pm-coach-sidebar {
          background: rgba(13,15,16,0.92);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 22px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.26);
        }
        .pm-coach-table-card {
          overflow: hidden;
        }
        .pm-coach-street-tabs {
          display: flex;
          gap: 10px;
          padding: 18px 18px 0;
          flex-wrap: wrap;
        }
        .pm-coach-street-tab {
          min-width: 112px;
          padding: 14px 18px;
          border-radius: 14px 14px 0 0;
          border: 1px solid rgba(255,255,255,0.08);
          border-bottom: none;
          background: rgba(255,255,255,0.03);
          color: rgba(255,255,255,0.58);
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.04em;
          cursor: pointer;
        }
        .pm-coach-street-tab.active {
          background: rgba(0,230,118,0.14);
          color: #2cf58d;
          box-shadow: inset 0 0 0 1px rgba(0,230,118,0.2);
        }
        .pm-coach-table-area {
          border-top: 1px solid rgba(255,255,255,0.08);
          padding: 24px 18px 18px;
        }
        .pm-coach-table-stage {
          position: relative;
          min-height: 540px;
          border-radius: 22px;
          background:
            radial-gradient(circle at 50% 8%, rgba(255,255,255,0.03), transparent 30%),
            linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0)),
            #080b0c;
          overflow: hidden;
        }
        .pm-coach-oval {
          position: absolute;
          inset: 110px 70px 78px;
          border-radius: 50%;
          background:
            radial-gradient(circle at 50% 32%, rgba(23,64,34,0.72), rgba(6,24,13,0.96)),
            #0c180f;
          border: 5px solid rgba(87,166,95,0.24);
          box-shadow:
            inset 0 0 40px rgba(0,0,0,0.44),
            0 0 0 1px rgba(255,255,255,0.04);
        }
        .pm-coach-seat {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          text-align: center;
        }
        .pm-coach-seat-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.08);
          background: #11151a;
          color: rgba(255,255,255,0.28);
          display: grid;
          place-items: center;
          font-size: 28px;
          font-weight: 700;
        }
        .pm-coach-seat-avatar.hero {
          color: #29f78d;
          border-color: rgba(0,230,118,0.5);
          box-shadow: 0 0 0 8px rgba(0,230,118,0.08);
        }
        .pm-coach-seat-avatar.villain {
          color: #9ab8ff;
          border-color: rgba(77,159,255,0.45);
        }
        .pm-coach-seat-name {
          font-size: 15px;
          font-weight: 700;
        }
        .pm-coach-seat-stack {
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          color: rgba(255,255,255,0.5);
          font-size: 12px;
          font-weight: 700;
        }
        .pm-coach-seat-tag {
          padding: 4px 10px;
          border-radius: 8px;
          background: rgba(255,184,0,0.12);
          border: 1px solid rgba(255,184,0,0.2);
          color: #ffb800;
          font-size: 12px;
          font-weight: 700;
        }
        .pm-coach-board-wrap {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          pointer-events: none;
        }
        .pm-coach-board-row,
        .pm-coach-hero-cards {
          display: flex;
          gap: 10px;
          align-items: center;
          justify-content: center;
        }
        .pm-coach-pot {
          padding: 8px 18px;
          border-radius: 999px;
          background: rgba(8,12,13,0.82);
          border: 1px solid rgba(255,255,255,0.08);
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.04em;
        }
        .pm-coach-pot strong {
          color: #29f78d;
        }
        .pm-coach-history-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px;
          border-top: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.02);
          flex-wrap: wrap;
        }
        .pm-coach-history-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          flex: 1;
        }
        .pm-coach-history-title {
          color: rgba(255,255,255,0.34);
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .pm-coach-history-chip {
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.04);
          color: rgba(255,255,255,0.8);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }
        .pm-coach-history-chip.selected {
          background: rgba(0,230,118,0.16);
          border-color: rgba(0,230,118,0.28);
          color: #29f78d;
        }
        .pm-coach-nav-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .pm-coach-nav-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.03);
          color: rgba(255,255,255,0.82);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }
        .pm-coach-nav-btn:disabled {
          cursor: not-allowed;
          opacity: 0.42;
        }
        .pm-coach-progress {
          color: rgba(255,255,255,0.46);
          font-size: 13px;
          font-weight: 700;
          min-width: 38px;
          text-align: center;
        }
        .pm-coach-sidebar {
          padding: 18px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          position: sticky;
          top: 20px;
        }
        .pm-coach-sidebar-title {
          display: flex;
          align-items: center;
          gap: 10px;
          color: rgba(255,255,255,0.7);
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .pm-coach-side-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 18px;
          padding: 16px;
        }
        .pm-coach-side-label {
          margin-bottom: 12px;
          color: rgba(255,255,255,0.4);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .pm-coach-side-copy {
          margin: 0;
          color: rgba(255,255,255,0.76);
          font-size: 14px;
          line-height: 1.65;
        }
        .pm-coach-side-copy.accent {
          margin-top: 10px;
          color: #ffb800;
        }
        .pm-coach-identity {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
        }
        .pm-coach-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: rgba(0,230,118,0.1);
          border: 1px solid rgba(0,230,118,0.3);
          color: #29f78d;
          font-size: 22px;
          font-weight: 800;
        }
        .pm-coach-identity-name {
          font-size: 22px;
          font-weight: 700;
        }
        .pm-coach-identity-meta {
          color: rgba(255,255,255,0.5);
          font-size: 13px;
        }
        .pm-coach-kv-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .pm-coach-kv-grid span {
          display: block;
          margin-bottom: 6px;
          color: rgba(255,255,255,0.34);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .pm-coach-kv-grid strong {
          color: rgba(255,255,255,0.88);
          font-size: 15px;
        }
        .pm-coach-badge-line {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }
        .pm-coach-badge {
          padding: 5px 9px;
          border-radius: 999px;
          background: rgba(255,184,0,0.14);
          border: 1px solid rgba(255,184,0,0.24);
          color: #ffb800;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
        }
        .pm-coach-small-note {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.5);
          font-size: 13px;
          line-height: 1.55;
        }
        .pm-coach-summary-card {
          margin-bottom: 16px;
          background: rgba(0,230,118,0.05);
          border: 1px solid rgba(0,230,118,0.14);
          border-radius: 16px;
          padding: 16px;
        }
        .pm-coach-summary-title {
          margin: 0 0 8px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #29f78d;
        }
        .pm-coach-summary-copy {
          margin: 0;
          color: rgba(255,255,255,0.74);
          line-height: 1.7;
          font-size: 14px;
        }
        .pm-coach-solver-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        @media (max-width: 1280px) {
          .pm-coach-main-grid {
            grid-template-columns: 1fr;
          }
          .pm-coach-sidebar {
            position: static;
          }
        }
        @media (max-width: 900px) {
          .pm-coach-shell {
            padding: 18px 14px 22px;
          }
          .pm-coach-topbar {
            flex-direction: column;
          }
          .pm-coach-actions {
            justify-content: flex-start;
          }
          .pm-coach-table-stage {
            min-height: 460px;
          }
          .pm-coach-oval {
            inset: 110px 18px 82px;
          }
        }
        @media (max-width: 640px) {
          .pm-coach-title {
            flex-wrap: wrap;
          }
          .pm-coach-title h1 {
            font-size: 28px;
          }
          .pm-coach-street-tab {
            min-width: calc(50% - 6px);
          }
          .pm-coach-table-stage {
            min-height: 420px;
          }
          .pm-coach-seat-avatar {
            width: 48px;
            height: 48px;
            font-size: 22px;
          }
          .pm-coach-seat-name {
            font-size: 13px;
          }
          .pm-coach-board-row {
            gap: 6px;
          }
          .pm-coach-solver-grid,
          .pm-coach-kv-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="pm-coach-shell">
        <div className="pm-coach-topbar">
          <div>
            <div className="pm-coach-title">
              <h1>Coach IA</h1>
              <div className="pm-coach-online">
                <span />
                Online
              </div>
            </div>
            <p className="pm-coach-subtitle">
              Reproduza a mão por street, troque o perfil do vilão e avance a linha
              só com controle manual do jogador.
            </p>
          </div>

          <div className="pm-coach-actions">
            <Link to="/sessions" className="pm-coach-ghost-btn">
              <History size={16} />
              Escolher outra mão
            </Link>
            <Link to="/sessions" className="pm-coach-primary-link">
              <Sparkles size={16} />
              Voltar para análises
            </Link>
          </div>
        </div>

        <div className="pm-coach-control-row">
          <div className="pm-coach-control-card">
            <label className="pm-coach-label" htmlFor="analysis-mode">
              Nível de Análise
            </label>
            <select
              id="analysis-mode"
              className="pm-coach-select"
              value={analysisMode}
              onChange={(event) => setAnalysisMode(event.target.value as AnalysisMode)}
            >
              {ANALYSIS_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
            <div className="pm-coach-helper">
              {ANALYSIS_MODES.find((mode) => mode.value === analysisMode)?.helper}
            </div>
          </div>

          <div className="pm-coach-control-card">
            <label className="pm-coach-label" htmlFor="villain-profile">
              Perfil do Vilão
            </label>
            <select
              id="villain-profile"
              className="pm-coach-select"
              value={villainProfileId}
              onChange={(event) => setVillainProfileId(event.target.value)}
            >
              {VILLAIN_PROFILES.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.label}
                </option>
              ))}
            </select>
            <div className="pm-coach-helper">{villainProfile.summary}</div>
          </div>

          <div className="pm-coach-control-card grow">
            <span className="pm-coach-label">Spot Carregado</span>
            <div className="pm-coach-pill-row">
              <div className="pm-coach-pill">{scenario.heroPosition}</div>
              <div className="pm-coach-pill">{scenario.villainPosition}</div>
              <div className="pm-coach-pill">{scenario.format}</div>
              <div className="pm-coach-pill">{scenario.modality}</div>
            </div>
            <div className="pm-coach-helper">
              {analysisMode === 'solver'
                ? 'No modo solver você pode trocar as cartas do herói e comparar como a linha muda.'
                : 'As streets abaixo só mudam no clique do jogador, sem avanço automático.'}
            </div>
          </div>
        </div>

        {analysisMode === 'solver' && (
          <div className="pm-coach-solver-row">
            <div className="pm-coach-solver-card">
              <span className="pm-coach-label">Mão Alternativa do Solver</span>
              <div className="pm-coach-solver-grid">
                <select
                  className="pm-coach-select"
                  value={solverCards[0]}
                  onChange={(event) =>
                    setSolverCards([event.target.value, solverCards[1]])
                  }
                >
                  {CARD_OPTIONS.filter(
                    (card) => card === solverCards[0] || card !== solverCards[1]
                  ).map((card) => (
                    <option key={card} value={card}>
                      {card.toUpperCase()}
                    </option>
                  ))}
                </select>
                <select
                  className="pm-coach-select"
                  value={solverCards[1]}
                  onChange={(event) =>
                    setSolverCards([solverCards[0], event.target.value])
                  }
                >
                  {CARD_OPTIONS.filter(
                    (card) => card === solverCards[1] || card !== solverCards[0]
                  ).map((card) => (
                    <option key={card} value={card}>
                      {card.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pm-coach-helper">{solverRead.detail}</div>
            </div>
          </div>
        )}

        <div className="pm-coach-main-grid">
          <div className="pm-coach-table-card">
            <div className="pm-coach-summary-card">
              <p className="pm-coach-summary-title">Resumo da mão</p>
              <p className="pm-coach-summary-copy">{scenario.summary}</p>
            </div>

            <div className="pm-coach-street-tabs">
              {(
                Object.keys(STREET_LABELS) as Street[]
              ).map((street) => (
                <button
                  key={street}
                  className={`pm-coach-street-tab ${
                    selectedStreet === street ? 'active' : ''
                  }`}
                  onClick={() => handleStreetClick(street)}
                >
                  {STREET_LABELS[street]}
                </button>
              ))}
            </div>

            <div className="pm-coach-table-area">
              <div className="pm-coach-table-stage">
                <div className="pm-coach-oval" />

                <div className="pm-coach-seat" style={{ top: 26, left: '50%', transform: 'translateX(-50%)' }}>
                  <div className="pm-coach-seat-avatar">P</div>
                  <div className="pm-coach-seat-name">Player 4</div>
                  <div className="pm-coach-seat-stack">UTG · 52k</div>
                </div>

                <div className="pm-coach-seat" style={{ top: 180, left: 42 }}>
                  <div className="pm-coach-seat-avatar">P</div>
                  <div className="pm-coach-seat-name">Player 5</div>
                  <div className="pm-coach-seat-stack">MP · 41k</div>
                </div>

                <div className="pm-coach-seat" style={{ bottom: 84, left: 110 }}>
                  <div className="pm-coach-seat-avatar">P</div>
                  <div className="pm-coach-seat-name">Player 6</div>
                  <div className="pm-coach-seat-stack">CO · 33k</div>
                </div>

                <div className="pm-coach-seat" style={{ top: 196, right: 54 }}>
                  <div className="pm-coach-seat-avatar villain">V</div>
                  <div className="pm-coach-seat-name">Villain</div>
                  <div className="pm-coach-seat-stack">
                    {scenario.villainPosition} · {scenario.villainStack}
                  </div>
                  <div className="pm-coach-seat-tag">{villainProfile.label}</div>
                </div>

                <div className="pm-coach-seat" style={{ bottom: 102, right: 126 }}>
                  <div className="pm-coach-seat-avatar">P</div>
                  <div className="pm-coach-seat-name">Player 2</div>
                  <div className="pm-coach-seat-stack">SB · 28k</div>
                </div>

                <div className="pm-coach-seat" style={{ bottom: 24, left: '50%', transform: 'translateX(-50%)' }}>
                  <div className="pm-coach-hero-cards">
                    {heroCards.map((card) => (
                      <PlayingCard key={card} value={card} size="md" />
                    ))}
                  </div>
                  <div className="pm-coach-seat-avatar hero">H</div>
                  <div className="pm-coach-seat-name">{scenario.heroName}</div>
                  <div className="pm-coach-seat-stack">
                    {scenario.heroPosition} · {scenario.heroStack}
                  </div>
                </div>

                <div className="pm-coach-board-wrap">
                  <div className="pm-coach-board-row">
                    {boardCards.map((card) => (
                      <PlayingCard key={card} value={card} size="md" />
                    ))}
                    {placeholderCards(Math.max(0, 5 - boardCards.length))}
                  </div>
                  <div className="pm-coach-pot">
                    POT: <strong>{scenario.potByStreet[selectedStreet]}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="pm-coach-history-bar">
              <div className="pm-coach-history-row">
                <div className="pm-coach-history-title">Histórico</div>
                {currentActions.map((action, index) => (
                  <button
                    key={action.id}
                    className={`pm-coach-history-chip ${
                      currentIndex === index ? 'selected' : ''
                    }`}
                    onClick={() => updateActionIndex(index)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>

              <div className="pm-coach-nav-controls">
                <button
                  className="pm-coach-nav-btn"
                  onClick={() => updateActionIndex(currentIndex - 1)}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft size={16} />
                  Anterior
                </button>
                <div className="pm-coach-progress">{actionProgress}</div>
                <button
                  className="pm-coach-nav-btn"
                  onClick={() => updateActionIndex(currentIndex + 1)}
                  disabled={currentIndex === currentActions.length - 1}
                >
                  Próxima
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

          <aside className="pm-coach-sidebar">
            <div className="pm-coach-sidebar-title">
              <Brain size={16} />
              Contexto
            </div>

            {renderProfileCards()}

            <div className="pm-coach-side-card">
              <div className="pm-coach-side-label">Ação Selecionada</div>
              <p className="pm-coach-side-copy">{selectedAction.label}</p>
              <div className="pm-coach-small-note">
                {analysisMode === 'solver'
                  ? solverRead.title
                  : actionCommentary}
              </div>
            </div>

            {analysisMode === 'solver' && (
              <div className="pm-coach-side-card">
                <div className="pm-coach-side-label">Comparativo Solver</div>
                <p className="pm-coach-side-copy">{solverRead.title}</p>
                <p className="pm-coach-side-copy accent">
                  {villainProfile.solverAdjustment}
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}
